import { ChangeDetectionStrategy, Component, computed, inject, signal, effect } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { provideNativeDateAdapter } from '@angular/material/core';

import { AuthService } from '../../../core/auth/auth.service';
import { SiteService } from '../../../core/services/site.service';
import { SensorManageService } from '../../../core/services/sensor-manage.service';
import { CsvExportService } from '../../../core/services/csv-export.service';
import { DataExtractService } from '../../../core/services/data-extract.service';

/** CSV共通22列ヘッダー */
const CSV_HEADERS: string[] = [
  '現場ID', '現場名', 'センサーID', '種別', '日時',
  'WBGT', '気温', '湿度', '風速', '風向',
  '騒音(Max)', '騒音(L5)', '振動(Max)', '振動(L10)',
  'pH', '傾斜X', '傾斜Y', '傾斜Z', '変位',
  'バッテリー', '通信状態', '備考'
];

/** 最大抽出期間（日数） */
const MAX_EXTRACT_DAYS = 31;

@Component({
  selector: 'app-data-extract',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatButtonModule,
    MatProgressBarModule,
    MatIconModule,
    MatSnackBarModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './data-extract.html',
  styleUrl: './data-extract.scss'
})
export class DataExtractComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly siteService = inject(SiteService);
  private readonly sensorManageService = inject(SensorManageService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly dataExtractService = inject(DataExtractService);
  private readonly snackBar = inject(MatSnackBar);

  public readonly form: FormGroup = this.fb.group({
    siteId: ['', Validators.required],
    from: [null, Validators.required],
    to: [null, Validators.required]
  });

  public readonly isExtracting = signal(false);
  public readonly maxDate = new Date();

  private readonly selectedSiteId = toSignal(this.form.get('siteId')!.valueChanges);

  public readonly sites = computed(() => {
    const role = this.authService.userRole();
    const allSites = this.siteService.sites();
    if (role === 'site_manager') {
      const uSiteId = this.authService.userSiteId();
      return allSites.filter(s => s.id === uSiteId);
    }
    return allSites;
  });

  public readonly availableSensors = computed(() => {
    const siteId = this.selectedSiteId();
    if (!siteId) return [];
    return this.sensorManageService.allSensors().filter(s => s.siteId === siteId);
  });

  public readonly selectedSensors = signal<Set<string>>(new Set());

  public readonly isAllSelected = computed(() => {
    const available = this.availableSensors();
    if (available.length === 0) return false;
    return available.every(s => this.selectedSensors().has(s.id));
  });

  public readonly isSomeSelected = computed(() => {
    const available = this.availableSensors();
    const selected = available.filter(s => this.selectedSensors().has(s.id));
    return selected.length > 0 && selected.length < available.length;
  });

  public readonly canExtract = computed(() => {
    const validForm = this.form.valid;
    const hasSensors = this.selectedSensors().size > 0;
    const isExtracting = this.isExtracting();
    return validForm && hasSensors && !isExtracting;
  });

  constructor() {
    effect(() => {
      // サイトが変更されたら選択中センサーをクリア
      this.selectedSiteId();
      this.selectedSensors.set(new Set());
    });
  }

  public toggleAllSensors(checked: boolean): void {
    if (checked) {
      const allIds = this.availableSensors().map(s => s.id);
      this.selectedSensors.set(new Set(allIds));
    } else {
      this.selectedSensors.set(new Set());
    }
  }

  public toggleSensor(sensorId: string, checked: boolean): void {
    const current = new Set(this.selectedSensors());
    if (checked) {
      current.add(sensorId);
    } else {
      current.delete(sensorId);
    }
    this.selectedSensors.set(current);
  }

  public async extractData(): Promise<void> {
    if (!this.canExtract()) return;

    const { siteId, from, to } = this.form.value;
    const selectedSensorIds = Array.from(this.selectedSensors());

    // バリデーション: 開始日 > 終了日のチェックは、MatDatepickerによって制御されフォームがinvalidになるためここでは不要
    const fromDate = new Date(from);
    const toDate = new Date(to);
    toDate.setHours(23, 59, 59, 999);

    const diffTime = toDate.getTime() - fromDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > MAX_EXTRACT_DAYS) {
      this.snackBar.open(`抽出期間は最大${MAX_EXTRACT_DAYS}日間までです`, '閉じる', { duration: 5000 });
      return;
    }

    this.isExtracting.set(true);

    try {
      // DataExtractService 経由で履歴データを取得
      const historiesMap = await this.dataExtractService.fetchSensorHistories(
        selectedSensorIds, fromDate, toDate
      );

      // 選択された現場情報を取得
      const site = this.sites().find(s => s.id === siteId);
      if (!site) {
        this.snackBar.open('現場情報が見つかりません', '閉じる', { duration: 5000 });
        return;
      }

      // 22列フォーマットに変換
      const allRows: string[][] = [];
      const sensors = this.availableSensors().filter(s => selectedSensorIds.includes(s.id));

      for (const sensor of sensors) {
        const histories = historiesMap.get(sensor.id) || [];
        const rows = this.dataExtractService.mapToExportRows(site, sensor, histories);
        allRows.push(...rows);
      }

      // 日時列（インデックス4）でソート
      allRows.sort((a, b) => {
        const dateA = a[4] || '';
        const dateB = b[4] || '';
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
      });

      const csvString = this.csvExportService.generateCsv(CSV_HEADERS, allRows);
      const filename = `data_extract_${siteId}_${this.formatDateForFilename(fromDate)}_${this.formatDateForFilename(toDate)}.csv`;

      this.csvExportService.downloadCsv(csvString, filename);
      this.snackBar.open('データを抽出しました', '閉じる', { duration: 3000 });

    } catch (err) {
      console.error('データ抽出に失敗しました:', err);
      this.snackBar.open('データの抽出に失敗しました', '閉じる', { duration: 5000 });
    } finally {
      this.isExtracting.set(false);
    }
  }

  private formatDateForFilename(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  }
}
